import { trackClientEvent } from 'analytics/analytics'
import { getCheckoutObservabilityState } from 'storage/checkout'

import {
  checkoutObservabilityContext,
  type CheckoutObservabilityContextData,
  type CheckoutObservabilityState,
} from './eventContexts/checkoutObservabilityContext'
import { checkoutSessionContext } from './eventContexts/checkoutSessionContext'

type CheckoutObservabilitySubject = 'attempt' | 'step' | 'module' | 'payment' | 'order'
type CheckoutObservabilityAction = 'entered' | 'exited_checkout' | 'restore_linked' | CheckoutObservabilityState

function isCheckoutObservabilityState(action: CheckoutObservabilityAction): action is CheckoutObservabilityState {
  return action !== 'entered' && action !== 'exited_checkout' && action !== 'restore_linked'
}

interface TrackCheckoutObservabilityEventParams extends Omit<CheckoutObservabilityContextData, 'state'> {
  subject: CheckoutObservabilitySubject
  action: CheckoutObservabilityAction
  type: 'interaction' | 'nonInteraction' | 'operational'
}

export type CheckoutValidationFailureContext = Omit<CheckoutObservabilityContextData, 'state' | 'isBlocking' | 'canContinue'>

export function trackCheckoutObservabilityEvent({ subject, action, type, ...contextData }: TrackCheckoutObservabilityEventParams): boolean {
  const attempt = getCheckoutObservabilityState().activeAttempt
  if (!attempt) return false

  trackClientEvent({
    category: 'checkout',
    subject,
    action,
    type,
    context: [
      checkoutSessionContext({
        checkoutSessionId: attempt.checkoutAttemptId,
        checkoutLineageId: attempt.checkoutLineageId,
      }),
      ...(isCheckoutObservabilityState(action)
        ? [checkoutObservabilityContext({ state: action, ...contextData })]
        : []),
    ],
  })

  return true
}

/**
 * Emits a single client event for one submit. Snowplow permits multiple
 * instances of the same context schema, preserving every co-occurring
 * validation failure under the event id.
 */
export function trackCheckoutValidationFailures(failures: ReadonlyArray<CheckoutValidationFailureContext>): boolean {
  const attempt = getCheckoutObservabilityState().activeAttempt
  if (!attempt || !failures.length) return false

  trackClientEvent({
    category: 'checkout',
    subject: 'step',
    action: 'validation_failed',
    type: 'nonInteraction',
    context: [
      checkoutSessionContext({
        checkoutSessionId: attempt.checkoutAttemptId,
        checkoutLineageId: attempt.checkoutLineageId,
      }),
      ...failures.map(failure => checkoutObservabilityContext({
        state: 'validation_failed',
        ...failure,
        isBlocking: true,
        canContinue: false,
      })),
    ],
  })

  return true
}
